import {useState} from 'react';
import {FileText, Folder, Loader2, Upload} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {toast} from '@common-web/hooks/use-toast';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {MAX_FTP_FILE_BYTES} from '@common/groupFtp/files';

const fileSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

const readBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// Upload a file straight into the group's folder on CBS's FTP as one of its documents.
export function UploadDocumentDialog({
  vbaGroupId,
  doc,
  onOpenChange,
}: {
  vbaGroupId: string;
  // folder: the group's subfolder the file goes in.
  doc: {id: string; label: string; folder: string};
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [reading, setReading] = useState(false);
  const tooBig = Boolean(file && file.size > MAX_FTP_FILE_BYTES);

  const upload = trpc.internal.groupFtp.uploadDocumentFile.useMutation({
    onSuccess: async ({fileName, folder}) => {
      await Promise.all([
        utils.internal.groupPipeline.detail.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.activity.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.list.invalidate(),
        utils.internal.groupPipeline.board.invalidate(),
        utils.internal.groupFtp.destinationFiles.invalidate({vbaGroupId}),
        utils.internal.groupFtp.missingFiles.invalidate({vbaGroupId}),
      ]);
      toast({title: `${fileName} uploaded to ${folder}`});
      onOpenChange(false);
    },
  });

  const submit = async () => {
    if (!file || tooBig) return;
    setReading(true);
    try {
      const base64 = await readBase64(file);
      upload.mutate({vbaGroupId, documentId: doc.id, fileName: file.name, base64});
    } finally {
      setReading(false);
    }
  };
  const busy = reading || upload.isPending;

  return (
    <Dialog open onOpenChange={(open) => !busy && onOpenChange(open)}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Upload {doc.label}</DialogTitle>
          <DialogDescription>
            Saves to the group's{' '}
            <span className='font-medium text-gray-700'>
              <Folder className='mr-1 inline h-3.5 w-3.5 align-[-2px]' />
              {doc.folder}
            </span>{' '}
            folder on CBS's FTP.
          </DialogDescription>
        </DialogHeader>

        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const dropped = e.dataTransfer.files[0];
            if (dropped) setFile(dropped);
          }}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed px-4 py-8 text-center text-sm transition-colors',
            dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:bg-gray-50',
          )}
        >
          <input
            type='file'
            className='sr-only'
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <>
              <FileText className='h-6 w-6 text-gray-400' />
              <span className='break-all font-medium'>{file.name}</span>
              <span className='text-xs text-gray-500'>
                {fileSize(file.size)} · Choose a different file
              </span>
            </>
          ) : (
            <>
              <Upload className='h-6 w-6 text-gray-400' />
              <span className='font-medium'>Drop a file here or choose one</span>
              <span className='text-xs text-gray-500'>
                Up to {MAX_FTP_FILE_BYTES / 1024 / 1024} MB
              </span>
            </>
          )}
        </label>

        {tooBig && (
          <p role='alert' className='text-sm text-red-700'>
            That file is over {MAX_FTP_FILE_BYTES / 1024 / 1024} MB.
          </p>
        )}
        {upload.error && (
          <p role='alert' className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
            {upload.error.message}
          </p>
        )}

        <DialogFooter>
          <Button variant='outline' disabled={busy} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!file || tooBig || busy} onClick={() => void submit()}>
            {busy && <Loader2 className='h-4 w-4 animate-spin' />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
